import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const METHOD_LABELS = { mpesa: "M-Pesa", card: "Card", snippe: "Snippe" };
const NEW_ADDR = { name: "", phone: "", city: "", region: "", country: "", address: "", landmark: "" };

export default function OrderDialog({ product, open, onOpenChange }) {
  const { user } = useAuth();
  const { t } = useLang();
  const { fmt } = useCurrency();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(product?.moq || 1);
  const [method, setMethod] = useState("mpesa");
  const [note, setNote] = useState("");
  const [methods, setMethods] = useState(["mpesa", "card", "snippe"]);
  const [addresses, setAddresses] = useState([]);
  const [mode, setMode] = useState("saved");
  const [selected, setSelected] = useState("");
  const [newAddr, setNewAddr] = useState(NEW_ADDR);
  const [saveAddr, setSaveAddr] = useState(true);
  const [loading, setLoading] = useState(false);
  const [placed, setPlaced] = useState(null);

  useEffect(() => {
    api("/config")
      .then((c) => Array.isArray(c.payments) && c.payments.length && setMethods(c.payments))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (open && user) {
      setNewAddr((a) => ({ ...a, name: a.name || user.name || "", country: a.country || user.country || "" }));
      api("/me/addresses", { auth: true })
        .then((list) => {
          setAddresses(list);
          const def = list.find((a) => a.is_default) || list[0];
          if (def) {
            setSelected(def.id);
            setMode("saved");
          } else {
            setMode("new");
          }
        })
        .catch(() => setMode("new"));
    }
  }, [open, user]);

  const unitPrice = product?.price_min ?? product?.price_max;
  const est = unitPrice != null && quantity ? fmt(unitPrice * Number(quantity)) : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      onOpenChange(false);
      navigate(`/login?next=/product/${product.id}`);
      return;
    }
    let delivery;
    if (mode === "saved") {
      const a = addresses.find((x) => x.id === selected);
      if (!a) {
        toast.error(t("delivery_addr"));
        return;
      }
      delivery = { name: a.name, phone: a.phone, city: a.city, region: a.region, country: a.country, address: a.address, landmark: a.landmark };
    } else {
      delivery = { ...newAddr };
    }
    setLoading(true);
    try {
      const res = await api("/orders", {
        method: "POST",
        auth: true,
        body: { product_id: product.id, quantity: Number(quantity), payment_method: method, note, source: "product", delivery },
      });
      if (mode === "new" && saveAddr) {
        api("/me/addresses", { method: "POST", auth: true, body: { ...newAddr, label: "", lat: null, lng: null, is_default: addresses.length === 0 } }).catch(() => {});
      }
      setPlaced(res);
      toast.success(t("order_placed"));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setPlaced(null);
  };

  const inputCls =
    "mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20";
  const setA = (k) => (e) => setNewAddr({ ...newAddr, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="order-dialog" className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        {placed ? (
          <div className="text-center py-4">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-teal-100 text-2xl text-teal-700">✓</div>
            <DialogHeader>
              <DialogTitle className="font-heading text-center">{t("order_placed")}</DialogTitle>
              <DialogDescription className="text-center">
                Order <span data-testid="order-confirmation-id" className="font-semibold text-teal-700">{(placed.id || placed.order_id || "").slice(-8).toUpperCase() || "✓"}</span> — {product?.supplier_name}
              </DialogDescription>
            </DialogHeader>
            <button
              data-testid="order-done-button"
              onClick={() => close(false)}
              className="mt-6 w-full rounded-lg bg-teal-700 py-2.5 text-sm font-semibold text-white hover:bg-teal-800 transition"
            >
              {t("done")}
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading">{t("place_order")}</DialogTitle>
              <DialogDescription>
                {product?.title} — {product?.supplier_name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700" htmlFor="order-qty">
                  {t("qty")} (MOQ {product?.moq} {product?.unit || "pcs"})
                </label>
                <input
                  id="order-qty"
                  data-testid="order-quantity-input"
                  type="number"
                  min={product?.moq || 1}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  required
                  className={inputCls}
                />
                {est && (
                  <p data-testid="order-estimate" className="mt-1 text-xs text-slate-500">
                    {t("est_total")}: <span className="font-semibold text-teal-700">{est}</span> {t("before_ship")}
                  </p>
                )}
              </div>

              {user && (
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">{t("delivery_addr")}</label>
                    {addresses.length > 0 && (
                      <button
                        type="button"
                        data-testid="order-new-address-toggle"
                        onClick={() => setMode(mode === "saved" ? "new" : "saved")}
                        className="text-xs font-semibold text-teal-700 hover:underline"
                      >
                        {mode === "saved" ? t("use_new") : t("use_saved")}
                      </button>
                    )}
                  </div>
                  {mode === "saved" && addresses.length > 0 ? (
                    <div className="mt-2 space-y-2">
                      {addresses.map((a) => (
                        <label
                          key={a.id}
                          data-testid={`order-address-${a.id}`}
                          className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                            selected === a.id ? "border-teal-500 bg-teal-50/60 ring-2 ring-teal-500/20" : "border-slate-200 hover:border-teal-300"
                          }`}
                        >
                          <input
                            type="radio"
                            name="order-address"
                            checked={selected === a.id}
                            onChange={() => setSelected(a.id)}
                            className="mt-1 h-4 w-4 accent-teal-600"
                          />
                          <span className="text-sm">
                            <span className="font-semibold text-slate-800">
                              {a.label || a.name}
                              {a.is_default && <span className="ml-2 rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-bold text-teal-700">Default</span>}
                            </span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              {a.name} · {a.phone} · {a.address}, {a.city}{a.country ? `, ${a.country}` : ""}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 space-y-3 rounded-xl border border-slate-200 p-3">
                      <div className="grid grid-cols-2 gap-3">
                        <input data-testid="order-address-name" required placeholder={t("full_name")} value={newAddr.name} onChange={setA("name")} className={inputCls} />
                        <input data-testid="order-address-phone" required placeholder={t("phone")} value={newAddr.phone} onChange={setA("phone")} className={inputCls} />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <input data-testid="order-address-city" required placeholder={t("country") === "Nchi" ? "Jiji" : "City"} value={newAddr.city} onChange={setA("city")} className={inputCls} />
                        <input data-testid="order-address-country" placeholder={t("country")} value={newAddr.country} onChange={setA("country")} className={inputCls} />
                      </div>
                      <input data-testid="order-address-street" required placeholder={t("delivery_addr")} value={newAddr.address} onChange={setA("address")} className={inputCls} />
                      <label className="flex items-center gap-2 text-xs text-slate-600">
                        <input data-testid="order-address-save" type="checkbox" checked={saveAddr} onChange={(e) => setSaveAddr(e.target.checked)} className="h-3.5 w-3.5 accent-teal-600" />
                        {t("save_addr")}
                      </label>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-700" htmlFor="order-method">
                  {t("payment_method")}
                </label>
                <select
                  id="order-method"
                  data-testid="order-payment-select"
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className={`${inputCls} bg-white`}
                >
                  {methods.map((m) => (
                    <option key={m} value={m} data-testid={`order-payment-${m}`}>
                      {METHOD_LABELS[m] || m}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700" htmlFor="order-note">
                  {t("note_supplier")}
                </label>
                <textarea
                  id="order-note"
                  data-testid="order-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  className={inputCls}
                />
              </div>
              {!user && (
                <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <MapPin className="h-3.5 w-3.5" /> {t("login_order")}
                </p>
              )}
              <button
                data-testid="order-submit-button"
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-teal-700 py-2.5 text-sm font-semibold text-white shadow-md shadow-teal-700/25 hover:bg-teal-800 transition active:scale-95 disabled:opacity-60"
              >
                {loading ? t("placing") : user ? t("place_order") : t("login_order")}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
